import '../assets/css/layout.css';
import '../assets/css/button.css';
import '../assets/css/tooltip.css';
import '../assets/css/textgen.css';

import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Select from '@radix-ui/react-select';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useTranslation } from 'react-i18next';

import { Copy } from '../Copy.tsx';
import { AVAILABLE_MODELS, DEFAULT_MODEL } from '../helpers.ts';
import { TypingMessageQueue } from '../TypingMessageQueue.ts';
import { EventSourceMessage, useEventSource } from '../useEventSource.ts';
import { AnimatedHeight } from './AnimatedHeight.tsx';
import DiffWords from './DiffWords.tsx';
import { Footer } from './Footer.tsx';
import GlobalContext from './GlobalContext.tsx';
import { Header } from './Header.tsx';
import { ChevronDown } from './icons/ChevronDown.tsx';
import { Close } from './icons/Close.tsx';
import { Illustration } from './icons/Illustration.tsx';
import { Pen } from './icons/Pen.tsx';
import { Remove } from './icons/Remove.tsx';
import { Reset } from './icons/Reset.tsx';
import { Validate } from './icons/Validate.tsx';
import MyMarkdown from './Markdown.tsx';
import { Tooltip } from './Tooltip.tsx';

const EVENT_SOURCE_NAME = 'default';

type Mode = 'en' | 'de' | 'fr';

// Payload of a single SSE message from /textgen/generate/
type SseMessageData = {
  response?: string;
  id?: string;
};

const MODE: { id: Mode; nativeName: string }[] = [
  { id: 'en', nativeName: 'English' },
  { id: 'de', nativeName: 'Deutsch' },
  { id: 'fr', nativeName: 'Français' },
  /*{ id: 'es', nativeName: 'Einfache Sprache (Richtlinien Kt. ZH)' },
  { id: 'ls', nativeName: 'Leichte Sprache (Richtlinien Kt. ZH)' },*/
];

const countWords = (text: string): number => {
  const trimmed = text.trim();
  if (trimmed === '') return 0;
  return trimmed.split(/\s+/).length;
};
/*
// the npm package is huge...
const countTokens = (content: string): number => {
  return tokenizer.encode(content).length
}*/

const TextGen = () => {
  const { t, i18n } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const { mainLanguage, org, host } = useContext(GlobalContext);
  const [userId, setUserId] = useState<string | null>(localStorage.getItem('uID') || sessionStorage.getItem('uID'));
  const [queryResult, setQueryResult] = useState<string>('');
  const [sseUri, setSseUri] = useState('');
  const [queryValue, setQueryValue] = useState('');
  const { close: eventSourceClose, start: eventSourceStart } = useEventSource();
  const [, setRequestId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [model, setModel] = useState<string>(() => {
    const storedModel = localStorage.getItem('textmate-model');
    return storedModel || DEFAULT_MODEL;
  });
  // Initialize mode from i18n language on mount
  const [mode, setMode] = useState<Mode>(() => {
    const detectedLang = (i18n.language?.split('-')[0] as Mode) || 'en';
    // Ensure detected language is supported, otherwise default to 'en'
    return ['en', 'de', 'fr'].includes(detectedLang) ? detectedLang : 'en';
  });
  const [u15y, setU15y] = useState<string | JSX.Element | null>(null);

  // Sync mode with i18n language on mount and when i18n changes
  useEffect(() => {
    const currentLang = (i18n.language?.split('-')[0] as Mode) || 'en';
    if (currentLang !== mode && ['en', 'de', 'fr'].includes(currentLang)) {
      setMode(currentLang);
    }
  }, [i18n.language, mode]);
  const [copied, setCopied] = useState(false);
  const [shortenWordCount, setShortenWordCount] = useState('');
  const [shortenWordCountForLabel, setShortenWordCountForLabel] = useState('');
  const [shortenMenuOpen, setShortenMenuOpen] = useState(false);
  const shortenInputRef = useRef<HTMLInputElement>(null);
  const resultContainer = useRef<HTMLDivElement>(null);

  const [showBanner, setShowBanner] = useState(true);
  const shortenWordLimit = useMemo(() => {
    const sanitized = shortenWordCount.trim();
    if (sanitized === '') {
      return null;
    }
    const parsedValue = parseInt(sanitized, 10);
    if (Number.isNaN(parsedValue) || parsedValue <= 0) {
      return null;
    }
    return parsedValue;
  }, [shortenWordCount]);

  /*const feedbackRef = useRef(null)
  const hintRef = useRef(null)
  const questionRef = useRef(null)
  const providerValue = useAccordionProvider({
    allowMultiple: true,
    transition: true,
    transitionTimeout: 450,
  })*/

  const getScrollBarWidth = () => {
    const outer = document.createElement('div');
    outer.style.visibility = 'hidden';
    outer.style.overflow = 'scroll';
    document.body.appendChild(outer);

    const inner = document.createElement('div');
    outer.appendChild(inner);

    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
    document.body.removeChild(outer);

    return scrollbarWidth;
  };

  useEffect(() => {
    document.documentElement.style.setProperty('--scrollbar-w', `${getScrollBarWidth()}px`);
  });

  const setNewEventSource = useCallback(
    (queryValue: string, model: string, id: string) => {
      if (queryValue && org) {
        const shortenParam = shortenWordLimit ? `&shorten_to_words=${shortenWordLimit}` : '';
        const source = `${host}/${org}/textgen/generate/?id=${id}&key=X9hL4Gp5W2D7eRtF&${
          userId ? `&uID=${userId}` : ''
        }&lang=${mainLanguage}&modelId=${model}&mode=${mode}${shortenParam}`;
        setSseUri(source);
      }
    },
    [org, host, userId, mainLanguage, mode, shortenWordLimit],
  );

  const parseMessageFromEventSource = useCallback(
    (parsedData: SseMessageData, queue: TypingMessageQueue) => {
      try {
        if (parsedData.response) {
          const answerEnd = () => {
            setIsLoading(false);
          };
          if (parsedData.response === '__THIS_IS_THE_ANSWER_END__') {
            answerEnd();
            return;
          }

          if (parsedData.response === '__THIS_IS_THE_END__') {
            eventSourceClose(EVENT_SOURCE_NAME);
            queue.enqueue(parsedData.response);
            queue.setThisIsTheEnd();
            setQueryValue('');
            answerEnd();
            return;
          }
          if (parsedData.response === '__CLR__') {
            queue.enqueue(parsedData.response);
            return;
          }

          queue.enqueue(parsedData.response);
          /*setQueryResult(queryResult => {
                        return queryResult.concat(parsedData.response)
                      })*/
        }
        if (parsedData.id) {
          setRequestId(parsedData.id);
        }
      } catch (error) {
        console.log(error);
      }
    },
    [eventSourceClose],
  );

  useEffect(() => {
    fetch(host + `/${org}/log`, {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ referrer: document.referrer || null, uID: userId }),
    })
      .then(async response => {
        const user = await response.json();
        if (user && user.id) {
          localStorage.setItem('uID', user.id);
          setUserId(user.id);
        }
      })
      .catch(error => {
        console.log('Error Log', error);
      });
  }, [host, org, userId]);

  useEffect(() => {
    try {
      if (!sseUri || !queryValue) {
        return;
      }
      setIsLoading(true);

      const queue = new TypingMessageQueue(setQueryResult);
      const onerror = (e: Event) => {
        // for some strange reason, we get an error event before the server sends the __THIS_IS_THE_END__ event
        // in some cases. We don't need to show that error, after we received some links... The main result is here anyway
        // For example: Ask for "Wer ist Inka?" on WintiGPT....
        console.log('SSE Error', e);
        eventSourceClose(EVENT_SOURCE_NAME);
        setQueryValue('');
        setIsLoading(false);
      };
      const onmessage = (event: EventSourceMessage) => {
        const parsedData = JSON.parse(event.data);

        parseMessageFromEventSource(parsedData, queue);
      };
      eventSourceStart(EVENT_SOURCE_NAME, sseUri, onmessage, onerror);

      return () => {
        eventSourceClose(EVENT_SOURCE_NAME);
      };
    } catch (error) {
      console.log(error);
    }
  }, [eventSourceClose, eventSourceStart, parseMessageFromEventSource, queryValue, sseUri, t]);

  useEffect(() => {
    if (shortenMenuOpen) {
      requestAnimationFrame(() => {
        if (shortenInputRef.current) {
          shortenInputRef.current.focus();
        }
      });
    } else {
      if (shortenWordCount) {
        setShortenWordCountForLabel(shortenWordCount);
      } else {
        setShortenWordCountForLabel('');
      }
    }
  }, [shortenMenuOpen, shortenWordCount]);

  const sendSubmit = (inputValue: string, model: string = '4o-8k') => {
    setCopied(false);
    const fetchData = async () => {
      fetch(`https://zix-api.dev.genai.liip.ch/understandability`, {
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
          //     Authorization: 'Basic c2ltcGxpZnk6M3NCT0N3ZzZRT2NiaGFyVmRFMEY=',
        },
        // credentials: 'include',
        body: JSON.stringify({ text: inputValue }),
      }).then(async response => {
        const LIMIT_HARD = 13.7;
        const LIMIT_MEDIUM = 15.7;
        const u15yData: {
          understandability: number;
          cefr_level: string;
        } = await response.json();

        const tooltipContent = `<b>Sprachniveaus:</b><br/> 
<span class="green">A1: Einfache Sätze <br/> 
A2: Kurze Alltagstexte <br/> 
B1: Sprache aus Alltag und Beruf </span></span><br/> 
<span class="orange">B2: Sachtexte und Standpunkte</span> <br/>
<span class="red">C1: Komplexe und literarische Texte <br/>
C2: Anspruchsvolle und abstrakte Texte</span> <br/>`;
        const getText = () => {
          // Only show u15y feedback in German for now (API returns German-specific content)
          if (i18n.language !== 'de') {
            return '';
          }
          if (u15yData.understandability < LIMIT_HARD) {
            return (
              <p>
                Dein Text ist <span className="red">schwer verständlich</span> und entspricht etwa dem Sprachniveau{' '}
                <span className="red">{u15yData?.cefr_level}</span>.{' '}
                <span data-tooltip-html={tooltipContent} className="question-mark-circle my-anchor-element">
                  i
                </span>
              </p>
            );
          }

          if (u15yData.understandability >= LIMIT_HARD && u15yData.understandability < LIMIT_MEDIUM) {
            return (
              <p>
                Dein Text ist <span className="orange">mässig verständlich</span> und entspricht etwa dem Sprachniveau{' '}
                <span className="orange">{u15yData?.cefr_level}</span>.{' '}
                <span data-tooltip-html={tooltipContent} className="question-mark-circle my-anchor-element">
                  i
                </span>
              </p>
            );
          }
          return (
            <p>
              Dein Text ist <span className="green">gut verständlich</span> und entspricht etwa dem Sprachniveau{' '}
              <span className="green">{u15yData?.cefr_level}</span>.{' '}
              <span data-tooltip-html={tooltipContent} className="question-mark-circle my-anchor-element">
                i
              </span>
            </p>
          );
        };
        setU15y(getText());
      });

      const payload: Record<string, string | number | null> = { query: inputValue, uID: userId };
      if (shortenWordLimit) {
        payload.shorten_to_words = shortenWordLimit;
      }

      const reponse = await fetch(
        host + `/${org}/textgen/store?key=X9hL4Gp5W2D7eRtF&${userId ? `&uID=${userId}` : ''}`,
        {
          method: 'post',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        },
      );

      setNewEventSource(inputValue, model, (await reponse.json()).id);
      setQueryResult(t('answers.oneMoment'));
    };
    if (inputValue.trim() === '') return;
    setQueryValue(inputValue);

    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('q', inputValue);
    fetchData();
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(event.target.value);
  };
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitted(true);

    if (window.innerWidth < 1024) {
      setTimeout(() => {
        resultContainer.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }

    sendSubmit(inputValue, model);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && event.metaKey) {
      event.preventDefault();
      handleSubmit(event);
    }
  };
  const queryResults = queryResult.split('******');
  const betterText = queryResults[0].trim();

  return (
    <main className="layout textGen" data-lang={mode}>
      <div className="layout__left container">
        <Header />
        <form onSubmit={handleSubmit} className="question">
          <div className="question__input-group">
            <label htmlFor="question" className="sr-only">
              {t('question.label')}
            </label>
            <textarea
              autoFocus
              placeholder={t('textgen.placeholder')}
              className="question__input"
              id="question"
              value={inputValue}
              onKeyDown={handleKeyDown}
              onChange={handleInputChange}
            ></textarea>

            <div className="question__settings">
              <div>
                <label htmlFor="language-switcher" className="sr-only">
                  {t('footer.selectLanguage')}
                </label>
                <Select.Root
                  value={mode}
                  onValueChange={value => {
                    const newMode = value as Mode;
                    setMode(newMode);
                    i18n.changeLanguage(newMode);
                    // Save preference to localStorage
                    localStorage.setItem('i18nextLng', newMode);
                  }}
                >
                  <Select.Trigger id="language-switcher" className="select" aria-label={t('footer.selectLanguage')}>
                    <Select.Value>
                      {t('textgen.inputLanguage')}: {MODE.find(m => m.id === mode)?.nativeName}
                    </Select.Value>
                    <Select.Icon className="select-icon">
                      <ChevronDown />
                    </Select.Icon>
                  </Select.Trigger>
                  <Select.Portal>
                    <Select.Content className="select-content" position="popper" sideOffset={4}>
                      <Select.Viewport className="select-viewport">
                        {MODE.map(model => (
                          <Select.Item key={model.id} value={model.id} className="select-item">
                            <Select.ItemText>{model.nativeName}</Select.ItemText>
                          </Select.Item>
                        ))}
                      </Select.Viewport>
                    </Select.Content>
                  </Select.Portal>
                </Select.Root>
              </div>
              <div>
                <label htmlFor="model-switcher" className="sr-only">
                  {t('footer.selectModel') || 'Select AI Model'}
                </label>
                <Select.Root
                  value={model}
                  onValueChange={(value: string) => {
                    setModel(value);
                    localStorage.setItem('textmate-model', value);
                  }}
                >
                  <Select.Trigger id="model-switcher" className="select" aria-label="AI Model">
                    <Select.Value>{AVAILABLE_MODELS.find(m => m.id === model)?.name || model}</Select.Value>
                    <Select.Icon className="select-icon">
                      <ChevronDown />
                    </Select.Icon>
                  </Select.Trigger>
                  <Select.Portal>
                    <Select.Content className="select-content" position="popper" sideOffset={4}>
                      <Select.Viewport className="select-viewport">
                        {AVAILABLE_MODELS.map(m => (
                          <Select.Item key={m.id} value={m.id} className="select-item">
                            <Select.ItemText>{m.name}</Select.ItemText>
                          </Select.Item>
                        ))}
                      </Select.Viewport>
                    </Select.Content>
                  </Select.Portal>
                </Select.Root>
              </div>
              <div>
                <DropdownMenu.Root open={shortenMenuOpen} onOpenChange={setShortenMenuOpen}>
                  <DropdownMenu.Trigger className="select question__max-length-dropdown">
                    <span>
                      {shortenWordCountForLabel
                        ? `${t('textgen.wordsMax')} ${shortenWordCountForLabel} ${t('textgen.wordsUnit')}`
                        : t('textgen.shortenCta')}
                    </span>
                    <span className="select-icon">
                      <ChevronDown />
                    </span>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Portal>
                    <DropdownMenu.Content className="select-content dropdown-menu-content" align="start" sideOffset={4}>
                      <label htmlFor="max-length">
                        <span>{t('textgen.shortenLabel')}</span>
                      </label>
                      <input
                        ref={shortenInputRef}
                        type="text"
                        inputMode="numeric"
                        id="max-length"
                        className="question__max-length-input"
                        aria-label={t('textgen.shortenLabel')}
                        placeholder={t('textgen.placeholderExample')}
                        value={shortenWordCount}
                        onChange={event => {
                          const value = event.target.value.replace(/[^\d\s]/g, '');
                          setShortenWordCount(value);
                        }}
                        onKeyDown={event => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            setShortenMenuOpen(false);
                          }
                        }}
                      />
                      <div
                        className={`question__max-length-button ${shortenWordCount.trim() ? 'question__max-length-button--visible' : ''}`}
                      >
                        <button
                          aria-label="Remove"
                          onClick={() => {
                            setShortenWordCount('');
                            setShortenMenuOpen(false);
                          }}
                        >
                          <Remove />
                        </button>
                        <button
                          aria-label="Validate"
                          onClick={() => {
                            setShortenMenuOpen(false);
                          }}
                        >
                          <Validate />
                        </button>
                      </div>
                    </DropdownMenu.Content>
                  </DropdownMenu.Portal>
                </DropdownMenu.Root>
              </div>
              <div className="question__word-count">
                {countWords(inputValue).toLocaleString('de-CH').replace(/\./g, "'")} {t('textgen.wordsUnit')}
              </div>
            </div>
          </div>

          <div className="question__footer">
            <div className="question__note">
              <p>{t('textgen.note')}</p>
              <p dangerouslySetInnerHTML={{ __html: t('textgen.sell') }} className="question__cta" />
            </div>
            <div className="question__actions">
              <button
                type="reset"
                className="button button--ghost button--icon button--no-wrap question__reset"
                disabled={inputValue.length === 0}
                onClick={() => {
                  setQueryResult('');
                  setU15y('');
                  setInputValue('');
                  setShortenWordCount('');
                  setShortenWordCountForLabel('');
                }}
              >
                <span>{t('textgen.reset')}</span>
                <Reset />
              </button>
              <button
                type="submit"
                className="button  button--icon button--no-wrap question__submit"
                disabled={inputValue.length === 0}
              >
                <span>{t('textgen.reviseText')}</span>
                <Pen />
              </button>
            </div>
          </div>
        </form>

        {t('header.privacy') && (
          <AnimatedHeight forceZero={!showBanner} className="privacy-animated-height">
            <div className="privacy-container">
              <div className="privacy-banner">
                <MyMarkdown markdown={t('header.privacy') || ''} />
                <button
                  className="privacy-banner__close"
                  onClick={() => setShowBanner(false)}
                  aria-label="Close banner"
                >
                  <Close className="privacy-banner__close-icon" />
                </button>
              </div>
            </div>
          </AnimatedHeight>
        )}
      </div>
      <div className="layout__right">
        <Tooltip />

        <div
          className={`result-box-container ${isSubmitted && 'result-box-container--submitted'}`}
          ref={resultContainer}
        >
          {!queryResult && <Illustration />}
          {queryResult && (
            <div className="result-box container" translate="no">
              {u15y && <div className={'u15y-feedback'}>{u15y}</div>}
              {queryResult && <h1 className="result-box__title">{t('answers.answer')}</h1>}
              <MyMarkdown markdown={queryResults[0]} />
              {queryResults[0] && queryResults.length > 1 && (
                <div className="result-box__copy">
                  <CopyToClipboard
                    text={queryResult.split('******')?.[0]?.trim() || ''}
                    onCopy={() => {
                      setCopied(true);

                      window.setTimeout(() => setCopied(false), 2000);
                    }}
                  >
                    <button className="button button--no-wrap">
                      <span>{copied ? t('textgen.copiedButton') : t('textgen.copyButton')}</span> <Copy />
                    </button>
                  </CopyToClipboard>
                  <p className="result-box__count">
                    <strong>{t('textgen.textLengthLabel')}:</strong>{' '}
                    {countWords(queryResults[0]).toLocaleString('de-CH').replace(/\./g, "'")} {t('textgen.wordsUnit')}
                  </p>
                </div>
              )}

              {queryResults.length > 1 &&
                inputValue.trim() !== betterText &&
                queryResults.slice(1).map(qr => (
                  <div className="resolut-box__table">
                    <MyMarkdown
                      markdown={
                        qr +
                        (isLoading
                          ? ' ![](https://chregus.rokka.io/dynamic/crop-width-200-height-25--resize-width-20/o-af-1/b9a927/circles-menu-1.gif)'
                          : '')
                      }
                    ></MyMarkdown>
                  </div>
                ))}

              {queryResults[0] && queryResults.length > 1 && (
                <div className="result-box__diff-container">
                  <h1 className="result-box__title">Difference</h1>
                  <div className="result-box__diff">
                    <DiffWords a={inputValue.trim()} b={betterText} />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <Footer />
      </div>
    </main>
  );
};
export default TextGen;
